import React, { useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Dropdown, Option, X, Input } from '../styled';
import { AutocompleteStyles } from '../types';
import { Portal } from './Portal';

interface VirtualDropdownProps {
  isOpen: boolean;
  filteredData: string[];
  value?: string[];
  onSelectOption: (option: string) => void;
  focusedIndex?: number;
  dropdownId?: string;
  styles?: AutocompleteStyles;
  small?: boolean;
  maxHeight?: number;
  itemHeight?: number;
  usePortal?: boolean;
  anchorElement?: React.RefObject<HTMLElement>;
  // New props for input in dropdown
  inputValue: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  placeholder?: string;
  disabled?: boolean;
}

const VirtualDropdown: React.FC<VirtualDropdownProps> = React.memo(
  ({
    isOpen,
    filteredData,
    value,
    onSelectOption,
    focusedIndex = -1,
    dropdownId,
    styles,
    small,
    maxHeight = 264,
    itemHeight = 24,
    usePortal = false,
    anchorElement,
    inputValue,
    onInputChange,
    onInputKeyDown,
    inputRef,
    placeholder,
    disabled = false,
  }) => {
      if (!isOpen || disabled) return null;

    // Extract complex expressions to separate variables
    const optionStyle = styles?.option;
    const inputStyle = styles?.input;

    const createOptionClickHandler = useCallback(
      (option: string) => {
        return () => onSelectOption(option);
      },
      [onSelectOption],
    );

    const ItemRenderer = useCallback(
      ({ index, style }: { index: number; style: React.CSSProperties }) => {
        // First item is the input
        if (index === 0) {
          return (
            <div style={style}>
              <Input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                value={inputValue}
                onChange={onInputChange}
                onKeyDown={onInputKeyDown}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-owns={dropdownId}
                aria-autocomplete="list"
                hasValue={false}
                style={{
                  ...inputStyle,
                  width: '100%',
                  borderRadius: '4px',
                  padding: '4px 8px',
                }}
                small={small}
                autoFocus
              />
            </div>
          );
        }

        // Subsequent items are options (adjust index by -1)
        const optionIndex = index - 1;
        const option = filteredData[optionIndex];
        const isSelected = value?.includes(option) ?? false;
        const isFocused = index === focusedIndex;

        return (
          <div style={style}>
            <Option
              key={option}
              id={dropdownId ? `${dropdownId}-option-${index}` : undefined}
              isFocused={isFocused}
              isSelected={isSelected}
              onClick={createOptionClickHandler(option)}
              role="option"
              aria-selected={isSelected}
              style={optionStyle}
            >
              {option}
              {isSelected && <X>✕</X>}
            </Option>
          </div>
        );
      },
      [
        filteredData,
        value,
        focusedIndex,
        dropdownId,
        optionStyle,
        createOptionClickHandler,
        inputValue,
        onInputChange,
        onInputKeyDown,
        inputRef,
        placeholder,
        inputStyle,
        small,
        isOpen,
      ],
    );

    if (!isOpen) return null;

    // Total items = 1 (input) + filtered options
    const totalItems = 1 + filteredData.length;
    const listHeight = Math.min(totalItems * itemHeight, maxHeight);

    const dropdownContent = (
      <Dropdown
        style={{
          ...styles?.dropdown,
          ...(usePortal && {
            position: 'relative',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            marginTop: '0px',
          }),
        }}
        id={dropdownId}
        small={small}
        role="listbox"
        aria-label="Options"
      >
        <List height={listHeight} itemCount={totalItems} itemSize={itemHeight} width="100%">
          {ItemRenderer}
        </List>
      </Dropdown>
    );

    if (usePortal && anchorElement) {
      return (
        <Portal isOpen={isOpen} anchorElement={anchorElement}>
          {dropdownContent}
        </Portal>
      );
    }

    return dropdownContent;
  },
);

export default VirtualDropdown;
